/**
 * Error Message Utilities
 *
 * Maps backend error messages to user-friendly messages
 * Provides consistent error handling across authentication flows
 */

/**
 * Map backend error to user-friendly message
 */
export function getUserFriendlyErrorMessage(error: any): string {
  // Handle different error formats
  const errorMessage = typeof error === 'string'
    ? error
    : error?.message || error?.detail || error?.error || 'An error occurred';

  const lowerError = errorMessage.toLowerCase();

  // Authentication errors
  if (lowerError.includes('incorrect email or password')) {
    return 'Invalid email or password. Please check your credentials and try again.';
  }

  if (lowerError.includes('email already registered')) {
    return 'This email is already registered. Please login or use a different email.';
  }

  if (lowerError.includes('email') && lowerError.includes('invalid')) {
    return 'Please enter a valid email address.';
  }

  if (lowerError.includes('password') && (lowerError.includes('weak') || lowerError.includes('short'))) {
    return 'Password must be at least 8 characters long and include uppercase, lowercase, number, and special character.';
  }

  if (lowerError.includes('user not found')) {
    return 'No account found with this email. Please check your email or sign up.';
  }

  if (lowerError.includes('inactive user') || lowerError.includes('account disabled')) {
    return 'Your account has been disabled. Please contact support for assistance.';
  }

  if (lowerError.includes('account temporarily locked') || lowerError.includes('account locked')) {
    return 'Your account is temporarily locked due to too many failed login attempts. Please try again in 15 minutes.';
  }

  if (lowerError.includes('invalid or expired reset token') || lowerError.includes('expired reset')) {
    return 'This password reset link is invalid or has expired. Please request a new one.';
  }

  // Rate limiting
  if (lowerError.includes('rate limit') || lowerError.includes('too many')) {
    return 'Too many attempts. Please wait a few minutes before trying again.';
  }

  // Network errors
  if (lowerError.includes('network') || lowerError.includes('fetch') || lowerError.includes('connection')) {
    return 'Network error. Please check your internet connection and try again.';
  }

  if (lowerError.includes('timeout')) {
    return 'Request timed out. Please try again.';
  }

  // Server errors
  if (lowerError.includes('500') || lowerError.includes('internal server')) {
    return 'Server error. Please try again in a few moments.';
  }

  if (lowerError.includes('503') || lowerError.includes('service unavailable')) {
    return 'Service temporarily unavailable. Please try again later.';
  }

  // Validation errors
  if (lowerError.includes('required')) {
    return 'Please fill in all required fields.';
  }

  if (lowerError.includes('validation')) {
    return 'Please check your input and try again.';
  }

  // Default fallback - return original message if it's user-friendly enough
  if (errorMessage.length < 100 && !errorMessage.includes('{') && !errorMessage.includes('undefined')) {
    return errorMessage;
  }

  // Generic fallback
  return 'An error occurred. Please try again or contact support if the problem persists.';
}

/**
 * Get error field for specific error types
 * Returns which form field the error should be associated with
 */
export function getErrorField(error: any): 'email' | 'password' | 'general' | null {
  const errorMessage = typeof error === 'string'
    ? error
    : error?.message || error?.detail || '';

  const lowerError = errorMessage.toLowerCase();

  // Credential errors involving both fields — show as global, not field-specific
  if (lowerError.includes('invalid email or password') ||
      lowerError.includes('incorrect email or password') ||
      lowerError.includes('incorrect') ||
      lowerError.includes('invalid credentials') ||
      lowerError.includes('account temporarily locked') ||
      lowerError.includes('account locked')) {
    return 'general';
  }

  if (lowerError.includes('email')) {
    return 'email';
  }

  if (lowerError.includes('password')) {
    return 'password';
  }

  return null;
}

/**
 * Determine if error should be shown as global message vs field-specific
 */
export function shouldShowAsGlobalError(error: any): boolean {
  const errorField = getErrorField(error);

  // Show as global if it's not field-specific
  return errorField === 'general' || errorField === null;
}
